//
// # GenAlpha Circuit Schema
//

// Local Imports
import * as core from "./core";
import { SchemaVersion } from "./version";

// # Circuit
// Transistor-level structural definition of a cell. Maps 1:1 to a SubcktDef
// in CDL/SPICE. A first-class content kind alongside Schematic and Layout.
export interface Circuit {
  contentKind: "circuit";
  schemaVersion: SchemaVersion;
  ident: core.Ident;
  pdkId?: string;
  schematicImpl?: core.Ident;
  ports: Array<string>; // Ordered port names, directly from .subckt header
  nets: Array<string>; // All nets: ports + internal (e.g. "net06", "net2")
  instances: Array<CircuitInstance>;
  properties?: Array<core.Property>;
}

// # Circuit Instance
// A device or subcircuit instance within a Circuit. Most instances are
// subcircuit calls (`X…`); how an instance netlists (a subcircuit `X…` call vs a
// primitive `M…`/`R…`/… card) is determined by the circuit it references — for a
// foundry device, the matching `ExternalCircuit`'s `spiceType`.
export interface CircuitInstance {
  name: string; // Instance name: "MM7"
  cell: string; // Cell being instantiated: "nmos_lvt", "pmos_lvt"
  connections: Array<CircuitConnection>; // Named port-to-net mapping
  params: Array<CircuitInstanceParam>;
}

// # SpiceType
// What a component is in SPICE — mirrors VLSIR's `SpiceType` (circuit.proto).
// Lives on the *definition* (an `ExternalCircuit`), not each instance. `"subckt"`
// is the default.
export type SpiceType =
  | "subckt"
  | "resistor"
  | "capacitor"
  | "inductor"
  | "mos"
  | "diode"
  | "bipolar"
  | "vsource"
  | "isource"
  | "vcvs"
  | "vccs"
  | "cccs"
  | "ccvs"
  | "tline";

// # External Circuit
// A circuit defined *outside* our netlister — a foundry device model, an LVS-deck
// subckt, or any cell from another tool's netlist. Mirrors VLSIR's
// `ExternalModule`: just the interface (name, ordered ports, params, spiceType),
// no body. When the writer hits an instance of an external circuit it emits the
// instance but NOT a definition — the body lives elsewhere (pulled in by a
// `.lib`/`.include`). A PDK keeps a list of these for its foundry devices.
export interface ExternalCircuit {
  name: string; // The circuit name as instantiated (the `cell` of its instances)
  desc?: string; // Optional human description
  ports: Array<string>; // Ordered ports (for order-sensitive netlist formats)
  params?: Array<CircuitInstanceParam>; // Parameters the circuit accepts
  spiceType?: SpiceType; // What it is in SPICE ("subckt" by default)
}

// # Circuit Connection
// Named port-to-net mapping — self-documenting, order-independent.
// For M* primitives: port names are drain/gate/source/bulk (SPICE convention).
export interface CircuitConnection {
  port: string; // Port name on the cell, e.g. "drain", "gate", "source", "bulk"
  net: string; // Net name in this circuit, e.g. "net06", "VDD"
}

// # Circuit Instance Parameter
// String-valued parameter — kept as-is from the CDL/AST (e.g. "81.0n", "3", "2*l").
export interface CircuitInstanceParam {
  name: string; // "w", "l", "nfin"
  value: string; // Raw string from AST kwargs
}
